import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Mail, User, Phone, MessageSquare, Send, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContactForm = ({ 
  selectedModel, 
  configurationType,
  length, 
  height, 
  priceData,
  includePosts,
  postSize,
  panels,
  gates,
  gatePostSize,
  gatePostQuantity,
  accessories,
  selectedColor
}) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedModel || !priceData) {
      toast({
        title: 'Fehler',
        description: 'Bitte wählen Sie zuerst ein Modell und konfigurieren Sie Ihren Zaun.',
        variant: 'destructive'
      });
      return;
    }

    // Validate dimensions based on configuration type
    const needsFenceDimensions = configurationType === 'fence-only' || configurationType === 'fence-and-gates';
    const needsGateDimensions = configurationType === 'gates-only' || configurationType === 'fence-and-gates';
    
    if (needsFenceDimensions && (!length || !height || parseInt(length) <= 0 || parseInt(height) <= 0)) {
      toast({
        title: 'Fehler',
        description: 'Bitte geben Sie die Zaunlänge und Zaunhöhe ein.',
        variant: 'destructive'
      });
      return;
    }
    
    if (needsGateDimensions) {
      // Check that ALL enabled gates have valid dimensions
      let hasAnyGateEnabled = false;
      let allEnabledGatesValid = true;
      
      if (gates?.pedestrian?.enabled) {
        hasAnyGateEnabled = true;
        let hasValidItem = false;
        if (gates.pedestrian.items) {
          for (const item of gates.pedestrian.items) {
            if (parseInt(item.width) > 0 && parseInt(item.height) > 0) {
              hasValidItem = true;
            }
          }
        }
        if (!hasValidItem) {
          allEnabledGatesValid = false;
        }
      }
      
      if (gates?.double?.enabled) {
        hasAnyGateEnabled = true;
        let hasValidItem = false;
        if (gates.double.items) {
          for (const item of gates.double.items) {
            if (parseInt(item.width) > 0 && parseInt(item.height) > 0) {
              hasValidItem = true;
            }
          }
        }
        if (!hasValidItem) {
          allEnabledGatesValid = false;
        }
      }
      
      if (gates?.sliding?.enabled) {
        hasAnyGateEnabled = true;
        let hasValidItem = false;
        if (gates.sliding.items) {
          for (const item of gates.sliding.items) {
            if (parseInt(item.width) > 0 && parseInt(item.height) > 0) {
              hasValidItem = true;
            }
          }
        }
        if (!hasValidItem) {
          allEnabledGatesValid = false;
        }
      }
      
      if (!hasAnyGateEnabled) {
        toast({
          title: 'Fehler',
          description: 'Bitte wählen Sie mindestens ein Tor aus.',
          variant: 'destructive'
        });
        return;
      }
      
      if (!allEnabledGatesValid) {
        toast({
          title: 'Fehler',
          description: 'Bitte geben Sie für alle ausgewählten Tore Breite und Höhe ein.',
          variant: 'destructive'
        });
        return;
      }
      
      // Validate gate post quantity if gate post size is selected
      if (gatePostSize && gatePostSize !== 'none') {
        if (!gatePostQuantity || parseInt(gatePostQuantity) <= 0) {
          toast({
            title: 'Fehler',
            description: 'Bitte geben Sie die Anzahl der Torsteher ein.',
            variant: 'destructive'
          });
          return;
        }
      }
    }

    if (!formData.name || !formData.email) {
      toast({
        title: 'Fehler',
        description: 'Bitte geben Sie Ihren Namen und E-Mail-Adresse ein.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const quoteData = {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone || null,
        customer_address: formData.address || null,
        configuration_type: configurationType,
        model_name: selectedModel.name,
        selected_color: selectedColor,
        fence_length: parseInt(length) || null,
        fence_height: parseInt(height) || null,
        include_posts: includePosts,
        post_size: postSize,
        panels: panels,
        gates: gates,
        gate_post_size: gatePostSize,
        gate_post_quantity: parseInt(gatePostQuantity) || null,
        accessories: accessories,
        price_data: priceData,
        message: formData.message || null
      };

      const response = await fetch(`${API_URL}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler beim Senden');
      }

      const result = await response.json();
      
      toast({
        title: 'Angebot gesendet! ✓',
        description: `Ihr Angebot wurde an ${formData.email} gesendet. Wir melden uns in Kürze bei Ihnen.`,
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast({
        title: 'Fehler',
        description: error.message || 'Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Card className="transition-all duration-300 hover:shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-emerald-600" />
          Angebot per E-Mail anfordern
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />
              Name *
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Ihr Name"
              className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-500" />
              E-Mail *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="ihre.email@beispiel.de"
              className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-500" />
              Telefon
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+49 123 456789"
              className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address" className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              Adresse
            </Label>
            <Input
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Straße, PLZ Ort"
              className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gray-500" />
              Nachricht
            </Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Zusätzliche Informationen oder Fragen..."
              rows={4}
              className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting || !selectedModel}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-6 text-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                Wird gesendet...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Angebot anfordern
              </span>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;